import {
  CarOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  WifiOutlined,
} from '@ant-design/icons'
import { Badge, Card, Col, Divider, Progress, Row, Statistic, Typography } from 'antd'
import React from 'react'
import './index.scss'

const { Title, Text } = Typography

const Dashboard: React.FC = () => {
  // 模拟数据
  const stats = {
    totalDevices: 1234,
    onlineDevices: 1089,
    firmwarePackages: 56,
    todayUpgrades: 23,
    successRate: 98.5,
    avgDownloadSpeed: 2.4,
  }

  const recentActivities = [
    { type: 'success', message: '车辆 VIN123456 升级成功', time: '2分钟前' },
    { type: 'warning', message: '固件包 v2.1.3 发布', time: '15分钟前' },
    { type: 'info', message: '新增设备 50 台', time: '1小时前' },
  ]

  return (
    <div className='dashboard'>
      <div className='dashboard-header'>
        <Title level={2}>
          <RiseOutlined className='header-icon' />
          {'  '}运营仪表盘
        </Title>
        <Text type='secondary'>实时监控车辆OTA升级状态和系统运行情况</Text>
      </div>

      {/* 核心指标卡片 */}
      <Row gutter={[24, 24]} className='stats-row'>
        <Col xs={24} sm={12} lg={6}>
          <Card className='stat-card stat-card-primary' hoverable>
            <div className='stat-content'>
              <div className='stat-icon primary'>
                <CarOutlined />
              </div>
              <div className='stat-info'>
                <Statistic
                  title='设备总数'
                  value={stats.totalDevices}
                  valueStyle={{ color: '#2c3e50', fontWeight: 600 }}
                  suffix='台'
                />
                <Text type='secondary' className='stat-desc'>
                  较昨日 +12
                </Text>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className='stat-card stat-card-success' hoverable>
            <div className='stat-content'>
              <div className='stat-icon success'>
                <WifiOutlined />
              </div>
              <div className='stat-info'>
                <Statistic
                  title='在线设备'
                  value={stats.onlineDevices}
                  valueStyle={{ color: '#27ae60', fontWeight: 600 }}
                  suffix='台'
                />
                <div className='stat-progress'>
                  <Progress
                    percent={Math.round((stats.onlineDevices / stats.totalDevices) * 100)}
                    size='small'
                    strokeColor='#27ae60'
                    showInfo={false}
                  />
                  <Text type='secondary' className='stat-desc'>
                    在线率 {Math.round((stats.onlineDevices / stats.totalDevices) * 100)}%
                  </Text>
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className='stat-card stat-card-info' hoverable>
            <div className='stat-content'>
              <div className='stat-icon info'>
                <CloudUploadOutlined />
              </div>
              <div className='stat-info'>
                <Statistic
                  title='固件包'
                  value={stats.firmwarePackages}
                  valueStyle={{ color: '#8e44ad', fontWeight: 600 }}
                  suffix='个'
                />
                <Text type='secondary' className='stat-desc'>
                  本月新增 8 个
                </Text>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className='stat-card stat-card-warning' hoverable>
            <div className='stat-content'>
              <div className='stat-icon warning'>
                <DownloadOutlined />
              </div>
              <div className='stat-info'>
                <Statistic
                  title='今日升级'
                  value={stats.todayUpgrades}
                  valueStyle={{ color: '#f39c12', fontWeight: 600 }}
                  suffix='次'
                />
                <Text type='secondary' className='stat-desc'>
                  成功率 {stats.successRate}%
                </Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 详细信息区域 */}
      <Row gutter={[24, 24]} style={{ marginTop: 32 }}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <div className='card-title'>
                <RiseOutlined />
                <span>升级成功率趋势</span>
              </div>
            }
            className='chart-card'
            extra={<Badge status='success' text={`当前成功率: ${stats.successRate}%`} />}
          >
            <div className='chart-placeholder'>
              <div className='chart-icon'>
                <RiseOutlined />
              </div>
              <div className='chart-text'>
                <Title level={4}>图表组件待集成</Title>
                <Text type='secondary'>将显示过去30天的升级成功率趋势图表</Text>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={
              <div className='card-title'>
                <SafetyCertificateOutlined />
                <span>系统状态</span>
              </div>
            }
            className='status-card'
          >
            <div className='status-item'>
              <div className='status-label'>
                <Badge status='success' />
                <span>API服务</span>
              </div>
              <Text strong>正常</Text>
            </div>
            <Divider />

            <div className='status-item'>
              <div className='status-label'>
                <Badge status='success' />
                <span>数据库</span>
              </div>
              <Text strong>正常</Text>
            </div>
            <Divider />

            <div className='status-item'>
              <div className='status-label'>
                <Badge status='success' />
                <span>CDN服务</span>
              </div>
              <Text strong>正常</Text>
            </div>
            <Divider />

            <div className='status-item'>
              <div className='status-label'>
                <Badge status='processing' />
                <span>平均下载速度</span>
              </div>
              <Text strong>{stats.avgDownloadSpeed} MB/s</Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <div className='card-title'>
                <DownloadOutlined />
                <span>下载速率监控</span>
              </div>
            }
            className='chart-card'
          >
            <div className='chart-placeholder'>
              <div className='chart-icon'>
                <DownloadOutlined />
              </div>
              <div className='chart-text'>
                <Title level={4}>实时监控图表</Title>
                <Text type='secondary'>显示实时下载速率和网络状况</Text>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <div className='card-title'>
                <ExclamationCircleOutlined />
                <span>最近活动</span>
              </div>
            }
            className='activity-card'
          >
            <div className='activity-list'>
              {recentActivities.map((activity, index) => (
                <div key={index} className='activity-item'>
                  <Badge status={activity.type as any} className='activity-badge' />
                  <div className='activity-content'>
                    <Text>{activity.message}</Text>
                    <Text type='secondary' className='activity-time'>
                      {activity.time}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default Dashboard
